var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { API_BASE_URL } from '../config/api';
/**
 * POST to /share-codes/save using multipart/form-data.
 * `raw_json` is attached as a JSON file so the server receives it as a file
 * upload rather than an inline JSON field.
 *
 * `shareOnly` marks a code that was minted by **Share**, not by Save. Sharing
 * has to persist the state (an unsaved code resolves to nothing for whoever
 * opens it), so the server holds the record either way — the flag is what tells
 * it to keep that record out of `GET /share-codes`, and therefore out of the
 * user's saved-work list. See docs/features/ShareOnlyCodes.md.
 *
 * Three states, and the third is the reason this is not a plain boolean:
 *
 *   `true`      — minted by Share; hide it.
 *   `false`     — a real Save; un-hide it. This is the ONLY way a shared code
 *                 becomes saved work, so every Save path sends it explicitly.
 *   omitted     — `share_only` is not sent at all and the stored value stands.
 *
 * Omission exists for writes that are not the user deciding to save: this
 * endpoint is also the only way to write a label, so `renameShareCodeLabel`
 * round-trips a record through here. Defaulting that to `false` would promote a
 * shared code to saved work as a side effect of renaming it — a mutation nobody
 * asked for.
 */
export function postShareCodeSave(params) {
    return __awaiter(this, void 0, void 0, function () {
        var sharecode, label, raw_json, shareOnly, formData;
        return __generator(this, function (_a) {
            sharecode = params.sharecode, label = params.label, raw_json = params.raw_json, shareOnly = params.shareOnly;
            formData = new FormData();
            formData.append('sharecode', sharecode);
            formData.append('label', label);
            // Only when the caller has an opinion — see the three states above.
            if (shareOnly !== undefined)
                formData.append('share_only', shareOnly ? '1' : '0');
            formData.append('raw_json', new Blob([JSON.stringify(raw_json)], { type: 'application/json' }), 'raw_json.json');
            return [2 /*return*/, fetch("".concat(API_BASE_URL, "share-codes/save"), {
                    method: 'POST',
                    credentials: 'include',
                    body: formData,
                })];
        });
    });
}
/**
 * Rename a saved share code's label.
 *
 * There is no label-only endpoint: `share-codes/save` is the only way to write
 * a label, and it takes the whole record. So this reads the stored `raw_json`
 * back and re-posts it unchanged alongside the new label.
 *
 * The read is deliberately NOT `fetchShareCodeData` — that helper substitutes a
 * blank save state when a code has no content yet, which is right for loading
 * the player but catastrophic here: re-posting the substitute would erase the
 * activity the row points at. We read the envelope directly and refuse to write
 * unless a real `raw_json` object came back.
 *
 * Returns true only when the label was actually persisted.
 */
export function renameShareCodeLabel(shareCode, label) {
    return __awaiter(this, void 0, void 0, function () {
        var res, body, rawJson, saveRes, _a;
        var _b, _c;
        return __generator(this, function (_d) {
            switch (_d.label) {
                case 0:
                    _d.trys.push([0, 4, , 5]);
                    return [4 /*yield*/, fetch("".concat(API_BASE_URL, "share-codes/").concat(encodeURIComponent(shareCode)), {
                            credentials: 'include',
                        })];
                case 1:
                    res = _d.sent();
                    if (!res.ok)
                        return [2 /*return*/, false];
                    return [4 /*yield*/, res.json().catch(function () { return null; })];
                case 2:
                    body = _d.sent();
                    rawJson = (_c = (_b = body === null || body === void 0 ? void 0 : body.data) === null || _b === void 0 ? void 0 : _b.raw_json) !== null && _c !== void 0 ? _c : body === null || body === void 0 ? void 0 : body.raw_json;
                    // No stored content — re-saving would overwrite the record with nothing.
                    if (!rawJson || typeof rawJson !== 'object')
                        return [2 /*return*/, false];
                    return [4 /*yield*/, postShareCodeSave({ sharecode: shareCode, label: label, raw_json: rawJson })];
                case 3:
                    saveRes = _d.sent();
                    return [2 /*return*/, saveRes.ok];
                case 4:
                    _a = _d.sent();
                    return [2 /*return*/, false];
                case 5: return [2 /*return*/];
            }
        });
    });
}
